/**
 * useDeviceInfo Hook
 * Collects device and location information for work session tracking
 *
 * This is the web implementation. Mobile apps should provide their own
 * implementation using native device APIs (expo-device, expo-location, etc.)
 */
import { WEB_GPS_OPTIONS, WEB_GPS_HARD_TIMEOUT_MS } from './gpsOptions';
import { useCallback, useMemo, useState, useEffect } from 'react';
// App version - should be injected from build config in real app
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';
/**
 * Parse user agent to extract browser and OS info
 */
function parseUserAgent(ua) {
    let browser = 'Unknown';
    let os = 'Unknown';
    // Detect browser
    if (ua.includes('Firefox/')) {
        browser = 'Firefox';
    }
    else if (ua.includes('Edg/')) {
        browser = 'Edge';
    }
    else if (ua.includes('Chrome/')) {
        browser = 'Chrome';
    }
    else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
        browser = 'Safari';
    }
    // Detect OS
    if (ua.includes('Windows NT')) {
        os = 'Windows';
    }
    else if (ua.includes('Mac OS X')) {
        os = 'macOS';
    }
    else if (ua.includes('Linux')) {
        os = 'Linux';
    }
    else if (ua.includes('Android')) {
        os = 'Android';
    }
    else if (ua.includes('iPhone') || ua.includes('iPad')) {
        os = 'iOS';
    }
    return { browser, os };
}
/**
 * Detect device type from user agent
 */
function detectDeviceType(ua) {
    if (/Mobi|Android.*Mobile|iPhone|iPod/i.test(ua)) {
        return 'mobile';
    }
    if (/Tablet|iPad|Android(?!.*Mobile)/i.test(ua)) {
        return 'tablet';
    }
    return 'desktop';
}
/**
 * Generate a simple device fingerprint based on browser characteristics
 * Note: This is a basic implementation. For production, consider using
 * a library like FingerprintJS for more robust fingerprinting.
 */
function generateFingerprint() {
    const components = [
        navigator.userAgent,
        navigator.language,
        screen.width,
        screen.height,
        screen.colorDepth,
        new Date().getTimezoneOffset(),
        navigator.hardwareConcurrency || 0,
        navigator.maxTouchPoints || 0,
    ];
    // Simple hash function
    const str = components.join('|');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
}
/**
 * Hook for collecting device and location information
 * Used to enrich work session data for tracking and fraud detection
 */
export function useDeviceInfo() {
    const [locationPermissionStatus, setLocationPermissionStatus] = useState('prompt');
    const isGeolocationAvailable = useMemo(() => {
        return typeof navigator !== 'undefined' && 'geolocation' in navigator;
    }, []);
    /**
     * Check current location permission status without prompting the user
     */
    const checkLocationPermission = useCallback(async () => {
        if (!isGeolocationAvailable) {
            setLocationPermissionStatus('unavailable');
            return 'unavailable';
        }
        // Check if Permissions API is available
        if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
            try {
                const result = await navigator.permissions.query({ name: 'geolocation' });
                const status = result.state;
                setLocationPermissionStatus(status);
                return status;
            }
            catch {
                // Permissions API not supported, assume 'prompt'
                return 'prompt';
            }
        }
        return 'prompt';
    }, [isGeolocationAvailable]);
    // Check permission status on mount
    useEffect(() => {
        checkLocationPermission();
    }, [checkLocationPermission]);
    const getDeviceInfo = useCallback(() => {
        if (typeof navigator === 'undefined') {
            return { device_type: 'desktop' };
        }
        const ua = navigator.userAgent;
        const { browser, os } = parseUserAgent(ua);
        const deviceType = detectDeviceType(ua);
        const fingerprint = generateFingerprint();
        return {
            device_fingerprint: fingerprint,
            device_name: browser,
            device_os: os,
            device_type: deviceType,
            app_version: APP_VERSION,
        };
    }, []);
    // Shared implementation for getLocationData/getClockInData: one position
    // read that reports both the coordinates AND why they're missing.
    const getLocationOutcome = useCallback(async () => {
        if (!isGeolocationAvailable) {
            return { location: null, failure: 'unsupported' };
        }
        // Check permission status first to avoid unnecessary prompts on clock out
        const currentStatus = await checkLocationPermission();
        if (currentStatus === 'denied') {
            // Permission was previously denied, don't prompt again
            return { location: null, failure: 'permission_denied' };
        }
        return new Promise((resolve) => {
            // Hard wall-clock ceiling: the spec `timeout` option does not cover the
            // permission-prompt phase, so a browser that never answers (ignored
            // prompt, geolocation stubbed to a no-op) invokes NEITHER callback and
            // this promise would never settle — callers awaiting it hang forever.
            // The settled flag makes late callbacks after the ceiling harmless.
            let settled = false;
            const settle = (outcome) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(hardTimeout);
                resolve(outcome);
            };
            const hardTimeout = setTimeout(() => {
                console.warn('Location request never answered (prompt ignored or geolocation stubbed)');
                settle({ location: null, failure: 'timeout' });
            }, WEB_GPS_HARD_TIMEOUT_MS);
            navigator.geolocation.getCurrentPosition((position) => {
                // Permission was granted
                setLocationPermissionStatus('granted');
                settle({
                    location: {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        gps_accuracy_meters: position.coords.accuracy,
                        altitude_meters: position.coords.altitude ?? undefined,
                    },
                    failure: null,
                });
            }, (error) => {
                // Handle different error codes
                if (error.code === error.PERMISSION_DENIED) {
                    setLocationPermissionStatus('denied');
                    console.info('Location permission denied by user');
                    settle({ location: null, failure: 'permission_denied' });
                }
                else if (error.code === error.TIMEOUT) {
                    console.warn('Location request timed out');
                    settle({ location: null, failure: 'timeout' });
                }
                else {
                    console.warn('Location information unavailable');
                    settle({ location: null, failure: 'unavailable' });
                }
            }, {
                ...WEB_GPS_OPTIONS,
            });
        });
    }, [isGeolocationAvailable, checkLocationPermission]);
    const getLocationData = useCallback(async () => {
        const { location } = await getLocationOutcome();
        return location;
    }, [getLocationOutcome]);
    const getClockInData = useCallback(async () => {
        const device = getDeviceInfo();
        const { location, failure } = await getLocationOutcome();
        return { device, location, locationFailure: failure };
    }, [getDeviceInfo, getLocationOutcome]);
    return {
        getDeviceInfo,
        getLocationData,
        isGeolocationAvailable,
        locationPermissionStatus,
        checkLocationPermission,
        getClockInData,
    };
}
